// src/api/memos.ts

import type {
    Memo,
    MemoAgentEntry,
    MemoCreate,
    MemoDataEntry
} from './types';
import { createAuthenticatedFetch } from './types/core/common';

import type { MemoWithRelations } from './types/memos/relations';

// Interface pour les erreurs API
interface ApiError {
  status?: number;
  message?: string;
}

export type {
    Memo, MemoAgentEntry, MemoCreate, MemoDataEntry, MemoWithRelations
};

const api = createAuthenticatedFetch();

/**
 * API principale pour les memos
 */

export const memos = {
  // ===============================
  // Nouvelles Méthodes Sécurisées
  // ===============================

  /**
   * Récupère les mémos de l'utilisateur connecté
   */
  async getMy(): Promise<Memo[]> {
    return api.get<Memo[]>('/memos/my');
  },

  /**
   * Récupère les mémos publics seulement
   */
  async getPublic(): Promise<Memo[]> {
    return api.get<Memo[]>('/memos/public');
  },

  /**
   * Récupère les mémos accessibles (utilisateur + publics)
   */
  async getAccessible(): Promise<Memo[]> {
    return api.get<Memo[]>('/memos/accessible');
  },

  /**
   * Récupère tous les mémos (admin seulement)
   */
  async getAllAdmin(): Promise<Memo[]> {
    return api.get<Memo[]>('/memos/all');
  },

  /**
   * Récupère tous les mémos, y compris les soft-deleted (admin seulement)
   */
  async getAllAdminWithDeleted(): Promise<Memo[]> {
    return api.get<Memo[]>('/memos/all_with_deleted');
  },

  /**
   * Récupère un memo par son UUID
   */
  async getByUuid(uuid: string): Promise<MemoWithRelations> {
    return api.get<MemoWithRelations>(`/memos/${uuid}`);
  },

  // ===============================
  // Méthodes CRUD Sécurisées
  // ===============================

  /**
   * Crée un nouveau memo
   */
  async createMemo(memo: MemoCreate): Promise<Memo> {
    return api.post<Memo>('/memos/', memo);
  },

  /**
   * Met à jour un memo existant
   */
  async updateMemo(uuid: string, memo: Partial<MemoCreate>): Promise<Memo> {
    return api.put<Memo>(`/memos/${uuid}`, memo);
  },

  /**
   * Supprime logiquement un memo
   */
  async deleteMemo(uuid: string): Promise<void> {
    return api.delete(`/memos/${uuid}`);
  },

  /**
   * Supprime définitivement un memo (hard delete)
   */
  async hardDeleteMemo(uuid: string): Promise<void> {
    return api.delete(`/memos/hard/${uuid}`);
  },

  /**
   * Restaure un memo supprimé logiquement
   */
  async restoreMemo(uuid: string): Promise<Memo> {
    return api.put<Memo>(`/memos/restore/${uuid}`, {});
  },

  /**
   * Vérifie si un mémo existe par son UUID, avec informations sur le statut de suppression et les permissions.
   */
  async checkMemoExists(uuid: string): Promise<{
    exists: boolean;
    is_deleted: boolean;
    is_owner: boolean;
    is_admin: boolean;
    can_restore: boolean;
    user_id?: string;
    title?: string;
  }> {
    return api.get(`/memos/exists/${uuid}`);
  },

  // ===============================
  // Méthodes Legacy (compatibilité)
  // ===============================

  /**
   * Récupère tous les memos d'un utilisateur
   */
  async fetchUserMemos(userId: string): Promise<Memo[]> {
    try {
      return await api.get(`/memos/user/${userId}`);
    } catch (error) {
      throw new Error("Erreur lors de la récupération des mémos de l'utilisateur");
    }
  },

  /**
   * Récupère un memo spécifique avec toutes ses relations
   */
  async fetchMemoWithRelations(uuid: string): Promise<MemoWithRelations> {
    try {
      return await api.get(`/memos/uuid/${uuid}`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Mémo non trouvé");
      }
      throw new Error("Erreur lors de la récupération du mémo et ses relations");
    }
  },

  /**
   * Crée un memo avec vérification des entités liées
   */
  async createMemoWithCompleteData(memoData: MemoCreate): Promise<Memo> {
    return await api.post('/memos/create_memos_data', memoData);
  },

  async fetchMemosUuid(uuid: string): Promise<Memo[]> {
    try {
      return await api.get(`/memos/uuid/${uuid}`);
    } catch (error) {
      console.error("Erreur :", error);
      throw new Error("Erreur lors de la récupération des mémos");
    }
  },

  /**
   * Récupère un memo par UUID (accès public ou par connaissance de l'UUID)
   */
  async getPublicByUuid(uuid: string): Promise<MemoWithRelations> {
    try {
      return await api.get(`/memos/public/${uuid}`);
    } catch (error: unknown) {
      if ((error as ApiError)?.status === 404) {
        throw new Error("Mémo non trouvé");
      }
      throw new Error("Erreur lors de la récupération du mémo");
    }
  }
};

/**
 * API pour les relations memo-agent
 */
export const memosAgents = {
  /**
   * Crée une nouvelle entrée de relation memo-agent
   */
  async createMemoAgentEntry(entry: MemoAgentEntry): Promise<MemoAgentEntry> {
    return await api.post('/memos-agents', entry);
  },

  /**
   * Récupère toutes les entrées d'agents pour un memo
   */
  async fetchMemoAgents(memoId: string): Promise<MemoAgentEntry[]> {
    try {
      return await api.get(`/memos-agents/memo/${memoId}`);
    } catch (error) {
      throw new Error("Erreur lors de la récupération des agents du mémo");
    }
  },

  /**
   * Met à jour une entrée memo-agent
   */
  async updateMemoAgentEntry(entryId: string, entry: Partial<MemoAgentEntry>): Promise<MemoAgentEntry> {
    return await api.put(`/memos-agents/${entryId}`, entry);
  },

  /**
   * Supprime une entrée memo-agent
   */
  async deleteMemoAgentEntry(entryId: string): Promise<void> {
    return api.delete(`/memos/agents/${entryId}`);
  },


};

// Exports des fonctions individuelles pour la rétrocompatibilité
export const {
  createMemo,
  updateMemo,
  fetchUserMemos,
  fetchMemoWithRelations,
  deleteMemo,
  createMemoWithCompleteData,
  fetchMemosUuid
} = memos;

export const {
  createMemoAgentEntry,
  fetchMemoAgents,
  updateMemoAgentEntry,
  deleteMemoAgentEntry
} = memosAgents;


